import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Eye, Package, Printer, ShoppingCart, Search, X, Image, Video, Link as LinkIcon, Play } from 'lucide-react';
import { FileUploader } from './FileUploader';
import { getImageUrl } from '@/utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminArtworks = ({ artworks, products, onRefresh }) => {
  const { token } = useAuth();
  const { t, i18n } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contentLang, setContentLang] = useState('zh');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [artistUsers, setArtistUsers] = useState([]);
  const [showCreateArtist, setShowCreateArtist] = useState(false);
  const [newArtistForm, setNewArtistForm] = useState({ name: '', email: '', password: '', bio: '' });
  const [creatingArtist, setCreatingArtist] = useState(false);
  const [formData, setFormData] = useState({
    title: '', title_en: '', title_ru: '',
    artist: '', artist_en: '', artist_ru: '',
    creator_id: '', // 关联艺术家用户ID
    description: '', description_en: '', description_ru: '',
    story: '', story_en: '', story_ru: '',
    image_url: '', images: [], video_url: '',
    year: '', medium: '', medium_en: '', medium_ru: '', dimensions: '',
    related_product_id: '', sort_order: 0, is_active: true,
    visible_languages: ['zh', 'en', 'ru']  // 语言可见性
  });

  // 获取所有艺术家用户（管理员专用API，包含邮箱）
  useEffect(() => {
    fetchArtistUsers();
  }, [token]);

  const fetchArtistUsers = async () => {
    try {
      // 使用管理员API获取艺术家列表（包含邮箱）
      const res = await axios.get(`${API}/admin/users/artists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtistUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch artists', err);
      // 如果管理员API失败，尝试公开API
      try {
        const fallback = await axios.get(`${API}/users/by-type/artist`);
        setArtistUsers(fallback.data);
      } catch (e) {
        console.error('Fallback also failed', e);
      }
    }
  };

  // 快速创建名家账号
  const handleCreateArtist = async () => {
    if (!newArtistForm.name) {
      toast.error('请输入名家姓名');
      return;
    }
    
    setCreatingArtist(true);
    try {
      // 如果没有填邮箱，自动生成一个
      const email = newArtistForm.email || `${Date.now()}@artcanvas.placeholder`;
      const password = newArtistForm.password || Math.random().toString(36).slice(-8);
      
      const res = await axios.post(`${API}/admin/users/create`, {
        name: newArtistForm.name,
        email: email,
        password: password,
        user_type: 'artist',
        bio: newArtistForm.bio || `著名艺术家${newArtistForm.name}的作品展示`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`名家账号"${newArtistForm.name}"创建成功！`);
      setShowCreateArtist(false);
      setNewArtistForm({ name: '', email: '', password: '', bio: '' });
      
      // 刷新艺术家列表并自动选中新创建的用户
      await fetchArtistUsers();
      if (res.data.user_id) {
        setFormData({ ...formData, creator_id: res.data.user_id, artist: newArtistForm.name });
      }
    } catch (err) {
      console.error('Failed to create artist', err);
      toast.error(err.response?.data?.detail || '创建失败');
    } finally {
      setCreatingArtist(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', title_en: '', title_ru: '',
      artist: '', artist_en: '', artist_ru: '',
      creator_id: '',
      description: '', description_en: '', description_ru: '',
      story: '', story_en: '', story_ru: '',
      image_url: '', images: [], video_url: '',
      year: '', medium: '', medium_en: '', medium_ru: '', dimensions: '',
      related_product_id: '', sort_order: 0, is_active: true,
      visible_languages: ['zh', 'en', 'ru'],
      is_original_for_sale: false, original_price: '', allow_merchandise: false,
      is_sold: false
    });
    setEditingArtwork(null);
    setContentLang('zh');
  };

  const openCreateDialog = () => { resetForm(); setDialogOpen(true); };

  const openEditDialog = (artwork) => {
    setEditingArtwork(artwork);
    setFormData({
      title: artwork.title || '', title_en: artwork.title_en || '', title_ru: artwork.title_ru || '',
      artist: artwork.artist || '', artist_en: artwork.artist_en || '', artist_ru: artwork.artist_ru || '',
      creator_id: artwork.creator_id || '',
      description: artwork.description || '', description_en: artwork.description_en || '', description_ru: artwork.description_ru || '',
      story: artwork.story || '', story_en: artwork.story_en || '', story_ru: artwork.story_ru || '',
      image_url: artwork.image_url || '', images: artwork.images || [], video_url: artwork.video_url || '',
      year: artwork.year || '', medium: artwork.medium || '', medium_en: artwork.medium_en || '', medium_ru: artwork.medium_ru || '',
      dimensions: artwork.dimensions || '', related_product_id: artwork.related_product_id || '',
      sort_order: artwork.sort_order || 0, is_active: artwork.is_active !== false,
      visible_languages: artwork.visible_languages || ['zh', 'en', 'ru'],
      is_original_for_sale: artwork.is_original_for_sale || false,
      original_price: artwork.original_price || '',
      allow_merchandise: artwork.allow_merchandise || false,
      is_sold: artwork.is_sold || false
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.image_url) {
      toast.error(t('admin.requiredFields', 'Please fill in required fields'));
      return;
    }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...formData,
        related_product_id: formData.related_product_id || null,
        original_price: formData.original_price === '' ? null : Number(formData.original_price) || null,
        creator_id: formData.creator_id || null,
      };
      if (editingArtwork) {
        await axios.put(`${API}/admin/artworks/${editingArtwork.id}`, payload, { headers });
        toast.success(t('admin.artworkUpdateSuccess'));
      } else {
        await axios.post(`${API}/artworks`, payload, { headers });
        toast.success(t('admin.artworkCreateSuccess'));
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : (typeof detail === 'string' ? detail : t('common.error'));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/artworks/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('admin.artworkDeleteSuccess', '画作已删除'));
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
    setDeleteTarget(null);
  };

  const addImage = (url) => {
    setFormData({ ...formData, images: [...formData.images, url] });
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const getField = (base) => contentLang === 'en' ? `${base}_en` : contentLang === 'ru' ? `${base}_ru` : base;

  return (
    <div data-testid="admin-artworks">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('admin.artworks')}</h1>
        <Button onClick={openCreateDialog} data-testid="create-artwork-btn">
          <Plus className="w-4 h-4 mr-2" /> {t('admin.addArtwork', 'Add Artwork')}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {artworks.length === 0 ? (
          <Card className="p-8 text-center text-amber-600 col-span-full">{t('admin.noArtworks', 'No artworks yet')}</Card>
        ) : (
          artworks.map((artwork) => (
            <Card key={artwork.id} className="overflow-hidden">
              <div className="aspect-square bg-amber-100">
                <img src={getImageUrl(artwork.image_url)} alt={artwork.title} className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold">{artwork.title}</h3>
                {artwork.artist && <p className="text-sm text-amber-600">{artwork.artist}</p>}
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Badge variant={artwork.is_active ? 'default' : 'secondary'}>
                    {artwork.is_active ? t('admin.active') : t('admin.inactive')}
                  </Badge>
                  {artwork.is_original_for_sale && (
                    <Badge className="bg-red-500 text-white">{artwork.original_price} 度电</Badge>
                  )}
                  {artwork.is_sold && (
                    <Badge variant="secondary">已售出</Badge>
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(artwork)}>
                    <Edit className="w-4 h-4 mr-1" /> {t('common.edit')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setDeleteTarget({ id: artwork.id, title: artwork.title })}>
                    <Trash2 className="w-4 h-4 mr-1 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.confirmDelete', '确认删除')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.confirmDeleteArtwork', '确定要删除这个画作吗？此操作无法撤销。')}
              {deleteTarget?.title && (
                <p className="mt-2 text-amber-700 text-sm">"{deleteTarget.title}"</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', '取消')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-500 hover:bg-red-600"
            >
              {t('common.delete', '删除')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArtwork ? t('admin.editArtwork', 'Edit Artwork') : t('admin.addArtwork', 'Add Artwork')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2 mb-4">
              {['zh', 'en', 'ru'].map((lang) => (
                <Button key={lang} variant={contentLang === lang ? 'default' : 'outline'} size="sm" onClick={() => setContentLang(lang)}>
                  {lang === 'zh' ? '中文' : lang === 'en' ? 'English' : 'Русский'}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.artworkTitle')} *</Label>
                <Input value={formData[getField('title')]} onChange={(e) => setFormData({ ...formData, [getField('title')]: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.artist', 'Artist')}</Label>
                <Input value={formData[getField('artist')]} onChange={(e) => setFormData({ ...formData, [getField('artist')]: e.target.value })} />
              </div>
            </div>

            {/* 代为上传 - 选择艺术家用户 */}
            <div className="space-y-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between">
                <Label className="text-amber-800">{t('admin.uploadForArtist', '代为上传（选择艺术家账号）')}</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowCreateArtist(true)}
                  className="text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  创建名家账号
                </Button>
              </div>
              <p className="text-xs text-amber-600 mb-2">{t('admin.uploadForArtistHint', '选择一位平台用户，此作品将关联到该用户名下')}</p>
              <Select 
                value={formData.creator_id || "none"} 
                onValueChange={(value) => {
                  const actualValue = value === "none" ? "" : value;
                  const selectedArtist = artistUsers.find(u => u.id === actualValue);
                  setFormData({ 
                    ...formData, 
                    creator_id: actualValue,
                    artist: selectedArtist ? (selectedArtist.name) : formData.artist
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.selectArtistOptional', '选择关联用户（可选）')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('admin.noUserLink', '不关联用户')}</SelectItem>
                  {artistUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} {user.email ? `(${user.email})` : ''} {user.managed_by_admin ? '👑' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('admin.artworkDesc')}</Label>
              <Textarea value={formData[getField('description')]} onChange={(e) => setFormData({ ...formData, [getField('description')]: e.target.value })} rows={3} />
            </div>

            <div className="space-y-2">
              <Label>{t('admin.artworkStory')}</Label>
              <Textarea value={formData[getField('story')]} onChange={(e) => setFormData({ ...formData, [getField('story')]: e.target.value })} rows={4} />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>{t('admin.mainImage', 'Main Image')} *</Label>
              <div className="flex gap-2">
                <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." className="flex-1" />
                <FileUploader type="image" accept="image/*" onUpload={(url) => setFormData({ ...formData, image_url: url })} label={t('admin.upload')} />
              </div>
              {formData.image_url && <img src={getImageUrl(formData.image_url)} alt="Main" className="w-32 h-32 object-cover rounded mt-2" />}
            </div>

            <div className="space-y-2">
              <Label>{t('admin.additionalImages', 'Additional Images')}</Label>
              <div className="flex flex-wrap gap-2">
                {formData.images.map((img, index) => (
                  <div key={index} className="relative">
                    <img src={getImageUrl(img)} alt="" className="w-20 h-20 object-cover rounded" />
                    <button onClick={() => removeImage(index)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs">×</button>
                  </div>
                ))}
                <FileUploader type="image" accept="image/*" onUpload={addImage} label="+" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('admin.videoUrl', 'Video URL')}</Label>
              <div className="flex gap-2">
                <Input value={formData.video_url} onChange={(e) => setFormData({ ...formData, video_url: e.target.value })} placeholder="https://..." className="flex-1" />
                <FileUploader type="video" accept="video/*" onUpload={(url) => setFormData({ ...formData, video_url: url })} label={t('admin.upload')} />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.artworkYear')}</Label>
                <Input value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} placeholder="2024" />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.artworkMedium')}</Label>
                <Input value={formData[getField('medium')]} onChange={(e) => setFormData({ ...formData, [getField('medium')]: e.target.value })} placeholder={contentLang === 'zh' ? '油画' : 'Oil on canvas'} />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.artworkDimensions')}</Label>
                <Input value={formData.dimensions} onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })} placeholder="60×80cm" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('admin.relatedProduct', 'Related Product (Tote Bag)')}</Label>
              <Select value={formData.related_product_id || 'none'} onValueChange={(v) => setFormData({ ...formData, related_product_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder={t('admin.selectProduct', 'Select a product')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('admin.noRelatedProduct', 'No related product')}</SelectItem>
                  {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.sortOrder')}</Label>
                <Input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.status')}</Label>
                <Select value={formData.is_active ? 'active' : 'inactive'} onValueChange={(v) => setFormData({ ...formData, is_active: v === 'active' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('admin.active')}</SelectItem>
                    <SelectItem value="inactive">{t('admin.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 销售设置 */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-green-700">💰 销售设置</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* 原作出售 */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_original_for_sale || false}
                        onChange={(e) => setFormData({ ...formData, is_original_for_sale: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm font-medium">原作出售</span>
                    </label>
                    {formData.is_original_for_sale && (
                      <div className="space-y-1">
                        <Label className="text-xs text-green-600">原作价格（度电）</Label>
                        <Input
                          type="number"
                          value={formData.original_price || ''}
                          onChange={(e) => setFormData({ ...formData, original_price: parseFloat(e.target.value) || 0 })}
                          placeholder="如：50000"
                          className="border-green-200"
                        />
                        {formData.original_price > 0 && (
                          <p className="text-xs text-green-500">
                            ≈ ${(formData.original_price * 0.1).toLocaleString()} USD | 艺术家获得 97% = {(formData.original_price * 0.97).toLocaleString()} 度电
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {/* 授权定制周边 */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allow_merchandise || false}
                        onChange={(e) => setFormData({ ...formData, allow_merchandise: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm font-medium">授权定制周边（布包等）</span>
                    </label>
                    {formData.allow_merchandise && (
                      <p className="text-xs text-green-500">艺术家获得衍生品销售额的 10%</p>
                    )}
                  </div>
                </div>
                {/* Sold Status (admin only, shown when editing) */}
                {editingArtwork && (
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="is-sold-toggle">
                      <input
                        type="checkbox"
                        checked={formData.is_sold || false}
                        onChange={(e) => setFormData({ ...formData, is_sold: e.target.checked, ...(e.target.checked ? {} : { is_original_for_sale: true }) })}
                        className="rounded"
                      />
                      <span className="text-sm font-medium text-red-600">Marked as Sold (已售出)</span>
                    </label>
                    {formData.is_sold && (
                      <p className="text-xs text-red-400 mt-1">
                        Uncheck to re-enable this artwork for sale. This will also set "Original For Sale" back to true.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 语言可见性设置 */}
            <Card className="bg-purple-50 border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-purple-700">🌐 {t('admin.languageVisibility', '语言版本可见性')}</CardTitle>
                <CardDescription className="text-xs">{t('admin.languageVisibilityDesc', '选择作品在哪些语言版本显示')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  {[
                    { code: 'zh', name: '中文', flag: '🇨🇳' },
                    { code: 'en', name: 'English', flag: '🇬🇧' },
                    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
                  ].map(lang => (
                    <label key={lang.code} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.visible_languages?.includes(lang.code)}
                        onChange={(e) => {
                          const newLangs = e.target.checked
                            ? [...(formData.visible_languages || []), lang.code]
                            : (formData.visible_languages || []).filter(l => l !== lang.code);
                          setFormData({ ...formData, visible_languages: newLangs });
                        }}
                        className="rounded"
                      />
                      <span>{lang.flag} {lang.name}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 创建名家账号弹窗 */}
      <Dialog open={showCreateArtist} onOpenChange={setShowCreateArtist}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>创建名家账号</DialogTitle>
            <DialogDescription>
              为知名艺术家（如毕加索、常玉等）创建代管账号，以便管理其作品展示
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>名家姓名 *</Label>
              <Input 
                value={newArtistForm.name} 
                onChange={(e) => setNewArtistForm({ ...newArtistForm, name: e.target.value })}
                placeholder="如：毕加索、常玉、吴冠中"
              />
            </div>
            <div className="space-y-2">
              <Label>邮箱（可选）</Label>
              <Input 
                value={newArtistForm.email} 
                onChange={(e) => setNewArtistForm({ ...newArtistForm, email: e.target.value })}
                placeholder="留空将自动生成占位邮箱"
              />
              <p className="text-xs text-amber-600">如需将账号转交给本人，请填写真实邮箱</p>
            </div>
            <div className="space-y-2">
              <Label>密码（可选）</Label>
              <Input 
                value={newArtistForm.password} 
                onChange={(e) => setNewArtistForm({ ...newArtistForm, password: e.target.value })}
                placeholder="留空将自动生成随机密码"
              />
            </div>
            <div className="space-y-2">
              <Label>简介（可选）</Label>
              <Textarea 
                value={newArtistForm.bio} 
                onChange={(e) => setNewArtistForm({ ...newArtistForm, bio: e.target.value })}
                placeholder="艺术家简介..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateArtist(false)}>取消</Button>
            <Button onClick={handleCreateArtist} disabled={creatingArtist}>
              {creatingArtist ? '创建中...' : '创建账号'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};



export default AdminArtworks;
